/**
 * 主客户端类
 * 移植自 xhshow-go/xhshow/client.go
 */

import { createHash } from 'crypto';
import { newSignatureData, XYS_PREFIX, X3_PREFIX } from './config.js';
import {
  encodeCustomBase64,
  encodeX3Base64,
  decodeCustomBase64,
  decodeX3Base64,
  generateB3TraceId,
  generateXrayTraceId,
  readUInt32LE,
  readUInt64LE,
} from './utils/index.js';
import { xorTransformArray, buildPayloadArray } from './crypto/index.js';
import { XsCommonSigner } from './xs-common.js';
import type { CookieDict, RequestPayload, SignatureData, X3Payload } from './types/index.js';

export class Client {
  private xsCommonSigner: XsCommonSigner;

  constructor() {
    this.xsCommonSigner = new XsCommonSigner();
  }

  /**
   * 获取 x-t 头部值（毫秒级 Unix 时间戳）
   */
  getXT(timestamp?: number): number {
    const ts = timestamp !== undefined ? timestamp : Date.now() / 1000;
    return Math.floor(ts * 1000);
  }

  /**
   * 获取 x-b3-traceid
   */
  getB3TraceId(): string {
    return generateB3TraceId();
  }

  /**
   * 获取 x-xray-traceid
   */
  getXrayTraceId(timestamp?: number, seq?: number): string {
    return generateXrayTraceId(timestamp, seq);
  }

  /**
   * 生成 x-s-common 签名
   */
  signXSCommon(cookies: CookieDict): string {
    return this.xsCommonSigner.sign(cookies);
  }

  /**
   * 生成请求签名 (x-s)
   */
  signXS(
    method: string,
    uri: string,
    a1Value: string,
    xsecAppId: string = 'xhs-pc-web',
    payload: RequestPayload = {},
    timestamp?: number
  ): string {
    const cleanUri = this.extractUri(uri);
    const contentString = this.buildContentString(method, cleanUri, payload);
    const dValue = this.generateDValue(contentString);

    const ts = timestamp !== undefined ? timestamp : Date.now();
    const sig = this.buildSignature(dValue, a1Value, xsecAppId, contentString, ts);

    const sigData = newSignatureData();
    sigData.x3 = X3_PREFIX + sig;

    // JSON 序列化（不转义 HTML）
    const jsonBytes = Buffer.from(JSON.stringify(sigData), 'utf8');
    const finalSig = XYS_PREFIX + encodeCustomBase64(jsonBytes);

    return finalSig;
  }

  /**
   * 构建内容字符串
   */
  private buildContentString(method: string, uri: string, payload: RequestPayload): string {
    method = method.toUpperCase();

    if (method === 'POST') {
      const jsonStr = JSON.stringify(payload);
      return uri + jsonStr;
    } else {
      if (Object.keys(payload).length === 0) {
        return uri;
      }

      // 对键进行排序
      const keys = Object.keys(payload).sort();
      const params: string[] = [];

      for (const k of keys) {
        const val = payload[k];
        let valStr: string;

        if (Array.isArray(val)) {
          // 用逗号连接
          valStr = val.map((v) => String(v)).join(',');
        } else {
          valStr = String(val);
        }

        // URL 编码（保留逗号）
        const encodedVal = this.pythonQuote(valStr);
        params.push(`${k}=${encodedVal}`);
      }

      return `${uri}?${params.join('&')}`;
    }
  }

  /**
   * 生成 D 值（MD5 哈希）
   */
  private generateDValue(content: string): string {
    const hash = createHash('md5').update(content, 'utf8').digest('hex');
    return hash;
  }

  /**
   * 构建签名
   */
  private buildSignature(
    dValue: string,
    a1Value: string,
    xsecAppId: string,
    stringParam: string,
    timestamp: number
  ): string {
    const payloadArray = buildPayloadArray(dValue, a1Value, xsecAppId, stringParam, timestamp);
    const xorResult = xorTransformArray(payloadArray);

    // 截断至 124 字节
    const truncated = xorResult.length > 124 ? xorResult.slice(0, 124) : xorResult;

    return encodeX3Base64(truncated);
  }

  /**
   * 提取 URI 路径
   */
  private extractUri(u: string): string {
    u = u.trim();

    // 如果以 / 开头，直接处理
    if (u.startsWith('/')) {
      const idx = u.indexOf('?');
      if (idx !== -1) {
        return u.substring(0, idx);
      }
      return u;
    }

    // 尝试解析 URL
    try {
      const url = new URL(u);
      return url.pathname || '/';
    } catch (e) {
      throw new Error(`cannot extract valid URI path from URL: ${u}`);
    }
  }

  /**
   * Python 风格的 quote (safe=",")
   */
  private pythonQuote(s: string): string {
    let res = encodeURIComponent(s);
    // encodeURIComponent 会把空格变成 %20，逗号变成 %2C
    // Python 保留逗号
    res = res.replace(/%2C/g, ',');
    return res;
  }

  /**
   * 解密完整的 XYS 签名
   */
  decodeXS(xsSignature: string): SignatureData {
    let sig = xsSignature;
    if (sig.startsWith(XYS_PREFIX)) {
      sig = sig.substring(XYS_PREFIX.length);
    }

    const jsonBytes = decodeCustomBase64(sig);
    const sigData: SignatureData = JSON.parse(Buffer.from(jsonBytes).toString('utf8'));

    return sigData;
  }

  /**
   * 解密 x3 签名
   */
  decodeX3(x3Signature: string): Uint8Array {
    let sig = x3Signature;
    if (sig.startsWith(X3_PREFIX)) {
      sig = sig.substring(X3_PREFIX.length);
    }

    const decodedBytes = decodeX3Base64(sig);

    // XOR 变换
    const intArr = Array.from(decodedBytes);
    const xorResult = xorTransformArray(intArr);

    return xorResult;
  }

  /**
   * 解析 X3 Payload
   */
  parseX3Payload(payload: Uint8Array): X3Payload {
    if (payload.length < 124) {
      throw new Error(`payload too short: ${payload.length} < 124`);
    }

    const res: X3Payload = {
      version: [],
      seed: 0,
      timestampRaw: 0,
      sequence: 0,
      windowPropsLen: 0,
      uriLen: 0,
      md5Hex: '',
      a1: '',
      source: '',
    };

    // 1. 版本 (4 字节)
    res.version = Array.from(payload.slice(0, 4));

    // 2. 随机种子 (4 字节小端序)
    res.seed = readUInt32LE(payload, 4);
    const seedByte0 = payload[4];

    // 4. 环境指纹 B (8 字节) - payload[16:24]
    res.timestampRaw = Number(readUInt64LE(payload, 16));

    // 5. 序列号 (4 字节) - payload[24:28]
    res.sequence = readUInt32LE(payload, 24);

    // 6. 窗口属性长度 (4 字节) - payload[28:32]
    res.windowPropsLen = readUInt32LE(payload, 28);

    // 7. URI 长度 (4 字节) - payload[32:36]
    res.uriLen = readUInt32LE(payload, 32);

    // 8. MD5 XOR 段 (8 字节) - payload[36:44]
    const md5Bytes = new Uint8Array(8);
    for (let i = 0; i < 8; i++) {
      md5Bytes[i] = payload[36 + i] ^ seedByte0;
    }
    res.md5Hex = Buffer.from(md5Bytes).toString('hex');

    // 10. A1 内容 (52 字节) - payload[45:97]
    const a1Bytes = payload.slice(45, 97);
    // 去除尾部的 null 字节
    const a1End = a1Bytes.indexOf(0);
    res.a1 = Buffer.from(a1Bytes.slice(0, a1End === -1 ? a1Bytes.length : a1End)).toString('utf8');

    // 12. 来源内容 (10 字节) - payload[98:108]
    const srcBytes = payload.slice(98, 108);
    const srcEnd = srcBytes.indexOf(0);
    res.source = Buffer.from(srcBytes.slice(0, srcEnd === -1 ? srcBytes.length : srcEnd)).toString(
      'utf8'
    );

    return res;
  }
}
